/**
 * Bedienfeld der Volumenstudie unter der 3D-Ansicht: die Baukörper mit Geschossen und Geschosshöhe, die Summen gegen
 * Grundstück und zulässiges Maß, Speichern. Gezeichnet wird über die Palette an der Seite der Karte
 * (Building3DControls). Reine Darstellung — Zustand und Karte liegen in Building3DView, die Rechnung in massing.ts.
 */
import { useTranslation } from "react-i18next";
import { Loader2, Trash2, Shapes } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { bodyMetrics, totalsOf, type MassingBody, type MassingContext, type Verdict } from "./massing";

interface Properties {
  context: MassingContext | null;
  contextLoading: boolean;
  bodies: MassingBody[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (body: MassingBody) => void;
  onRemove: (id: string) => void;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  /** Freifläche des Grundstücks (ohne Bestand und Baukörper), wenn „Freiflächen“ an ist. */
  openSpace: { areaM2: number } | null;
  /** Abstandsflächen je Baukörper, wenn der Schalter an ist. */
  setbacks: Array<{ name: string; depthM: number; heightM: number; factor: number; minM: number; law: string; outsideM2: number }>;
}

const de = (value: number | null | undefined, digits = 0) =>
  typeof value === "number" && Number.isFinite(value) ? value.toLocaleString("de-DE", { minimumFractionDigits: digits, maximumFractionDigits: digits }) : "–";
const span = (range: { low: number; high: number } | null | undefined, digits = 2) =>
  range ? (Math.abs(range.high - range.low) < 1e-9 ? de(range.high, digits) : `${de(range.low, digits)}–${de(range.high, digits)}`) : "–";

const VERDICT_CLASS: Record<Verdict, string> = {
  ok: "text-emerald-700",
  range: "text-amber-700",
  over: "text-destructive font-semibold",
  unknown: "",
};

export default function MassingPanel(properties: Properties) {
  const { t } = useTranslation();
  const p = properties;
  const totals = totalsOf(p.bodies, p.context);
  const allowed = p.context?.allowed ?? null;
  const parcel = p.context?.parcel ?? null;

  const numberInput = (body: MassingBody, field: "storeys" | "storeyHeightM", step: number, min: number, max: number) => (
    <Input
      type="number"
      inputMode="decimal"
      step={step}
      min={min}
      max={max}
      className="h-8 w-20"
      value={body[field]}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => {
        const value = Number(event.target.value);
        if (Number.isFinite(value)) p.onChange({ ...body, [field]: Math.min(max, Math.max(min, field === "storeys" ? Math.round(value) : value)) });
      }}
    />
  );

  return (
    <section className="mt-3 flex min-w-0 flex-col gap-3 rounded-lg border bg-card p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Shapes aria-hidden className="h-4 w-4 text-muted-foreground" />
          {t("building3D.massing.title")}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!p.dirty || p.saving} onClick={p.onSave}>
            {p.saving ? <Loader2 aria-hidden className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {t("building3D.massing.save")}
          </Button>
        </div>
      </header>

      <p className="text-xs text-muted-foreground">
        {p.contextLoading ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 aria-hidden className="h-3 w-3 animate-spin" />
            {t("building3D.massing.contextLoading")}
          </span>
        ) : parcel ? (
          <>
            {t("building3D.massing.parcel", { number: parcel.number ?? "–", gemarkung: parcel.gemarkung ?? "", area: de(parcel.areaM2) })}
            {allowed ? (
              <>
                {" · "}
                {allowed.basis === "BP"
                  ? t("building3D.massing.basisPlan", { name: allowed.planName ?? "" })
                  : allowed.basis === "34"
                    ? t("building3D.massing.basis34")
                    : allowed.basis === "35"
                      ? t("building3D.massing.basis35")
                      : t("building3D.massing.basisUnknown")}
                {allowed.reserveBasis && allowed.reserveBasis !== "B-Plan" ? ` (${t("building3D.massing.measureFromSurroundings")})` : ""}
                {allowed.uses.length ? ` · ${allowed.uses.join("/")}` : ""}
              </>
            ) : null}
          </>
        ) : (
          t("building3D.massing.noParcel")
        )}
      </p>

      {p.bodies.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("building3D.massing.intro")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-1">{t("building3D.massing.body")}</th>
                <th className="px-2 py-1">{t("building3D.massing.storeys")}</th>
                <th className="px-2 py-1">{t("building3D.massing.storeyHeight")}</th>
                <th className="px-2 py-1 text-right">{t("building3D.massing.footprint")}</th>
                <th className="px-2 py-1 text-right">{t("building3D.massing.floorArea")}</th>
                <th className="px-2 py-1 text-right">{t("building3D.massing.height")}</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {p.bodies.map((body) => {
                const m = bodyMetrics(body);
                return (
                  <tr
                    key={body.id}
                    className={"cursor-pointer border-t " + (body.id === p.selectedId ? "bg-accent" : "hover:bg-accent/50")}
                    onClick={() => p.onSelect(body.id === p.selectedId ? null : body.id)}
                  >
                    <td className="px-2 py-1">
                      <Input
                        className="h-8 min-w-28"
                        value={body.name}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => p.onChange({ ...body, name: event.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">{numberInput(body, "storeys", 1, 1, 60)}</td>
                    <td className="px-2 py-1">{numberInput(body, "storeyHeightM", 0.1, 2, 8)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{de(m.footprintM2)} m²</td>
                    <td className="px-2 py-1 text-right tabular-nums">{de(m.floorAreaM2)} m²</td>
                    <td className="px-2 py-1 text-right tabular-nums">{de(m.heightM, 1)} m</td>
                    <td className="px-2 py-1 text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        aria-label={t("building3D.massing.remove")}
                        title={t("building3D.massing.remove")}
                        onClick={(event) => {
                          event.stopPropagation();
                          p.onRemove(body.id);
                        }}
                      >
                        <Trash2 aria-hidden className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {p.bodies.length ? (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-5">
          <div>
            <dt className="text-xs text-muted-foreground">{t("building3D.massing.footprint")}</dt>
            <dd className="tabular-nums">{de(totals.footprintM2)} m²</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("building3D.massing.floorArea")}</dt>
            <dd className="tabular-nums">{de(totals.floorAreaM2)} m²</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">GRZ</dt>
            <dd className={"tabular-nums " + VERDICT_CLASS[totals.verdict.grz]}>
              {de(totals.grz, 2)} <span className="text-xs text-muted-foreground">/ {span(allowed?.grz)}</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">GFZ</dt>
            <dd className={"tabular-nums " + VERDICT_CLASS[totals.verdict.gfz]}>
              {de(totals.gfz, 2)} <span className="text-xs text-muted-foreground">/ {span(allowed?.gfz)}</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("building3D.massing.storeysMax")}</dt>
            <dd className={"tabular-nums " + VERDICT_CLASS[totals.verdict.storeys]}>
              {totals.maxStoreys}{" "}
              <span className="text-xs text-muted-foreground">
                / {allowed?.storeys ? (allowed.storeys.max && allowed.storeys.max !== allowed.storeys.value ? `${allowed.storeys.value}–${allowed.storeys.max}` : allowed.storeys.value) : "–"}
              </span>
            </dd>
          </div>
        </dl>
      ) : null}
      {p.openSpace && parcel ? (
        <div className="flex flex-col gap-1 border-t pt-3 text-sm">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-xs text-muted-foreground">{t("building3D.massing.openSpace")}</span>
            <span
              className={
                "tabular-nums " +
                (p.context?.openSpaceMin
                  ? p.openSpace.areaM2 + 0.5 < p.context.openSpaceMin.openM2Min
                    ? VERDICT_CLASS.over
                    : VERDICT_CLASS.ok
                  : "")
              }
            >
              {de(p.openSpace.areaM2)} m² ({de((p.openSpace.areaM2 / parcel.areaM2) * 100)} %)
            </span>
            {p.context?.openSpaceMin ? (
              <span className="text-xs text-muted-foreground">
                {t("building3D.massing.openSpaceMin", { m2: de(p.context.openSpaceMin.openM2Min), share: de(p.context.openSpaceMin.openShareMin * 100) })}
              </span>
            ) : null}
          </div>
          {p.context?.planFeatures?.length ? (
            <span className="text-xs text-muted-foreground">
              {t("building3D.massing.planFeatures", {
                list: Object.entries(
                  p.context.planFeatures.reduce((acc: Record<string, number>, f) => ({ ...acc, [f.label]: (acc[f.label] ?? 0) + 1 }), {}),
                )
                  .map(([label, n]) => (n > 1 ? `${label} (${n})` : label))
                  .join(", "),
              })}
            </span>
          ) : null}
          <span className="text-xs text-muted-foreground">{t("building3D.massing.openSpaceNote")}</span>
        </div>
      ) : null}
      {p.setbacks.length ? (
        <div className="flex flex-col gap-1 border-t pt-3 text-sm">
          <span className="text-xs text-muted-foreground">{t("building3D.massing.setbacks")}</span>
          {p.setbacks.map((x) => (
            <span key={x.name} className={x.outsideM2 > 0.5 ? VERDICT_CLASS.over : ""}>
              {t("building3D.massing.setbackLine", {
                name: x.name,
                depth: de(x.depthM, 1),
                factor: de(x.factor, 1),
                height: de(x.heightM, 1),
                min: de(x.minM, 1),
                law: x.law,
              })}
              {x.outsideM2 > 0.5 ? ` — ${t("building3D.massing.setbackOutside", { m2: de(x.outsideM2) })}` : ` — ${t("building3D.massing.setbackInside")}`}
            </span>
          ))}
          <span className="text-xs text-muted-foreground">{t("building3D.massing.setbackNote")}</span>
        </div>
      ) : null}
      {p.bodies.length ? <p className="text-xs text-muted-foreground">{t("building3D.massing.footnote")}</p> : null}
      {p.error ? <p className="text-xs text-destructive">{p.error}</p> : null}
    </section>
  );
}
